import { useProject } from '../stores/ProjectContext'
import { useI18n } from '../stores/I18nContext'
import './InsightsPanel.css'

export default function InsightsPanel({ isOpen, onClose }) {
  const { project } = useProject()
  const { language } = useI18n()
  const isNorwegian = language === 'nb'

  if (!isOpen) return null

  // Calculate insights from project data
  const calculateInsights = () => {
    const flows = project?.flows || []
    const allCheckpoints = flows.flatMap(f =>
      (f.checkpoints || []).map(cp => ({ ...cp, flowId: f.id, flowName: f.name, flowColor: f.color }))
    )

    // Count completed checkpoints
    const completed = allCheckpoints.filter(cp => cp.status === 'completed').length
    const total = allCheckpoints.length
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0

    // Find conflicts (checkpoints at same time/distance)
    const conflicts = []
    for (let i = 0; i < allCheckpoints.length; i++) {
      for (let j = i + 1; j < allCheckpoints.length; j++) {
        const a = allCheckpoints[i]
        const b = allCheckpoints[j]
        if (a.time === b.time && a.distance === b.distance) {
          conflicts.push({ a, b })
        }
      }
    }

    // Find longest waiting time between checkpoints in same flow
    let maxWait = 0
    flows.forEach(flow => {
      const sorted = [...(flow.checkpoints || [])].sort((a, b) => a.time - b.time)
      for (let i = 1; i < sorted.length; i++) {
        const wait = sorted[i].time - sorted[i - 1].time
        if (wait > maxWait) maxWait = wait
      }
    })

    // Calculate critical path (longest flow)
    let criticalPath = { name: '', duration: 0 }
    flows.forEach(flow => {
      const sorted = [...(flow.checkpoints || [])].sort((a, b) => a.time - b.time)
      if (sorted.length > 0) {
        const duration = sorted[sorted.length - 1].time - sorted[0].time
        if (duration > criticalPath.duration) {
          criticalPath = { name: flow.name, duration }
        }
      }
    })

    return {
      flowCount: flows.length,
      checkpointCount: total,
      completed,
      completionRate,
      conflictsList: conflicts,
      conflictsCount: conflicts.length,
      maxWait,
      criticalPath
    }
  }

  const insights = calculateInsights()
  const timeUnit = project?.timeUnit || 'weeks'
  const timeUnitLabel = isNorwegian
    ? (timeUnit === 'weeks' ? 'uker' : timeUnit === 'days' ? 'dager' : timeUnit)
    : (timeUnit === 'weeks' ? 'weeks' : timeUnit === 'days' ? 'days' : timeUnit)

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="insights-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isNorwegian ? 'Innsikter' : 'Insights'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="insights-grid">
            <div className="insight-card">
              <div className="insight-icon">📊</div>
              <div className="insight-content">
                <div className="insight-value">{insights.flowCount}</div>
                <div className="insight-label">{isNorwegian ? 'Flyter' : 'Flows'}</div>
              </div>
            </div>

            <div className="insight-card">
              <div className="insight-icon">◇</div>
              <div className="insight-content">
                <div className="insight-value">{insights.checkpointCount}</div>
                <div className="insight-label">{isNorwegian ? 'Sjekkpunkter' : 'Checkpoints'}</div>
              </div>
            </div>

            <div className="insight-card">
              <div className="insight-icon">✓</div>
              <div className="insight-content">
                <div className="insight-value">{insights.completionRate}%</div>
                <div className="insight-label">{isNorwegian ? 'Fullført' : 'Completed'}</div>
              </div>
            </div>

            <div className={`insight-card ${insights.conflictsCount > 0 ? 'warning' : ''}`}>
              <div className="insight-icon">⚠️</div>
              <div className="insight-content">
                <div className="insight-value">{insights.conflictsCount}</div>
                <div className="insight-label">{isNorwegian ? 'Konflikter' : 'Conflicts'}</div>
              </div>
            </div>
          </div>

          <div className="insights-section">
            <h3>{isNorwegian ? 'Detaljer' : 'Details'}</h3>

            <div className="insight-detail">
              <span className="detail-label">{isNorwegian ? 'Maks ventetid' : 'Max waiting time'}:</span>
              <span className="detail-value">{insights.maxWait} {timeUnitLabel}</span>
            </div>

            <div className="insight-detail">
              <span className="detail-label">{isNorwegian ? 'Kritisk sti' : 'Critical path'}:</span>
              <span className="detail-value">
                {insights.criticalPath.name || (isNorwegian ? 'Ingen' : 'None')}
                {insights.criticalPath.duration > 0 && ` (${insights.criticalPath.duration} ${timeUnitLabel})`}
              </span>
            </div>

            <div className="insight-detail">
              <span className="detail-label">{isNorwegian ? 'Fremgang' : 'Progress'}:</span>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${insights.completionRate}%` }}
                />
              </div>
              <span className="detail-value">{insights.completed}/{insights.checkpointCount}</span>
            </div>
          </div>

          {insights.conflictsCount > 0 && (
            <div className="insights-section conflicts-section">
              <h3>
                <span className="warning-icon">⚠️</span>
                {isNorwegian ? 'Konflikter' : 'Conflicts'}
              </h3>
              <p className="section-description">
                {isNorwegian
                  ? 'Følgende sjekkpunkter overlapper på samme tid og avstand:'
                  : 'The following checkpoints overlap at the same time and distance:'}
              </p>
              <div className="conflicts-list">
                {insights.conflictsList.map((conflict, index) => (
                  <div key={index} className="conflict-item">
                    <div className="conflict-header">
                      <span className="conflict-number">#{index + 1}</span>
                      <span className="conflict-location">
                        {isNorwegian ? 'Tid' : 'Time'}: {conflict.a.time}, {isNorwegian ? 'Avstand' : 'Distance'}: {conflict.a.distance}
                      </span>
                    </div>
                    <div className="conflict-checkpoints">
                      <div className="conflict-checkpoint">
                        <div className="checkpoint-flow" style={{ backgroundColor: conflict.a.flowColor }}>
                          {conflict.a.flowName}
                        </div>
                        <div className="checkpoint-name">{conflict.a.name}</div>
                      </div>
                      <div className="conflict-vs">⚡</div>
                      <div className="conflict-checkpoint">
                        <div className="checkpoint-flow" style={{ backgroundColor: conflict.b.flowColor }}>
                          {conflict.b.flowName}
                        </div>
                        <div className="checkpoint-name">{conflict.b.name}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>
            {isNorwegian ? 'Lukk' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  )
}
